import { useState } from 'react'

import { StepDialogConfig, StepDialogResult } from './type'

/**
 * Creates a fully type-safe step dialog hook
 * Invalid transitions are detected at compile time
 */
export function createStepDialog<const TConfig extends StepDialogConfig>(
  config: TConfig
) {
  type Step = TConfig['steps'][number]
  type Transitions = TConfig['transitions']

  // Calculate allowed destinations type from each step
  type AllowedDestinations<From extends Step> = From extends keyof Transitions
    ? Transitions[From][number]
    : never

  return function useStepDialog(
    initialStep?: Step
  ): StepDialogResult<Step, AllowedDestinations<Step>> {
    const [step, setStep] = useState<Step>(initialStep || config.initial)

    const transitionTo = <To extends AllowedDestinations<typeof step>>(
      to: To
    ) => {
      setStep(to)
      return true
    }

    const reset = () => {
      setStep(config.initial)
    }

    const is = <T extends Step>(targetStep: T): boolean => {
      return step === targetStep
    }

    return {
      step,
      transitionTo,
      reset,
      is,
    }
  }
}
